import { useTimeZone } from '@/auth/useTimeZone';
import { InfoPage } from '@/components/info-page';
import {
  SessionScheduleCalendar,
  coachCalendarSupportedViews,
  eventsFromSessions,
  type CalendarView,
} from '@/components/session/session-schedule-calendar';
import { EmptyArray, getCoachPortalCalendarSessions } from '@/lib/api-calls';
import { SessionScheduleCalendarTimezonePill } from '@/components/session/session-schedule-calendar-timezone-pill';
import { useErrorToast } from '@/lib/error-handling';
import {
  coachCalendarDefaultSessionRemarksV2,
  normalizeCoachCalendarView,
  type CoachCalendarRouteConstraint,
} from '@/lib/my-classes-utils';
import { fetchAllPages } from '@/lib/pagination';
import { RegisteredRouteTree, RoutesWithValidConstraint, useNavigateSearchState } from '@/lib/routing';
import { instantFromStringWithDefault, type InstantInterval } from '@airreading/common';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi, RouteById } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';

export type CoachClassesCalendarCompatibleRouteId = RoutesWithValidConstraint<CoachCalendarRouteConstraint>;

export interface CoachClassesCalendarProps {
  route: RouteById<RegisteredRouteTree, CoachClassesCalendarCompatibleRouteId>;
}

export function CoachClassesCalendar({ route }: CoachClassesCalendarProps) {
  const { timeZone } = useTimeZone();
  const calendarRoute = getRouteApi(route.id);
  const navigate = calendarRoute.useNavigate();
  const [instantInterval, setInstantInterval] = useState<InstantInterval | null>(null);

  const { data, isFetching, error } = useQuery({
    ...getCoachPortalCalendarSessions({
      ...(instantInterval && {
        startOnOrAfter: instantInterval.start.toString(),
        startOnOrBefore: instantInterval.end.toString(),
        sessionRemarksV2: coachCalendarDefaultSessionRemarksV2.join(','),
        includeSessionsWithoutSessionRemarksV2: 'true',
      }),
      ...fetchAllPages,
    }),
  });

  useErrorToast(error, 'Error loading classes. Please try again.');

  const sessions = data?.coachSessions ?? EmptyArray;
  const { events, sessionById } = useMemo(() => eventsFromSessions(sessions, timeZone), [sessions, timeZone]);

  const [view, setView] = useNavigateSearchState(route.id, 'view');
  const calendarView = normalizeCoachCalendarView(view);

  const onView = useCallback(
    (newView: CalendarView) => {
      setView(newView);
    },
    [setView],
  );

  const [instantQueryString, setInstantQueryString] = useNavigateSearchState(route.id, 'date');
  const activeInstant = useMemo(
    () => instantFromStringWithDefault(instantQueryString, Temporal.Now.instant()),
    [instantQueryString],
  );
  const setActiveInstant = useCallback(
    (newInstant: Temporal.Instant) => {
      setInstantQueryString(newInstant.toString());
    },
    [setInstantQueryString],
  );

  const navigateToDayAt = useCallback(
    (instant: Temporal.Instant) => {
      navigate({
        search: (prev) => ({
          ...prev,
          view: 'day',
          date: instant.toString(),
        }),
      });
    },
    [navigate],
  );

  return (
    <InfoPage headerText="My classes" data-testid="sessions-calendar-page">
      <div className="flex h-[85vh] min-h-0 min-w-0 max-w-full flex-col">
        <SessionScheduleCalendar
          isLoading={isFetching}
          events={events}
          sessionById={sessionById}
          activeInstant={activeInstant}
          setActiveInstant={setActiveInstant}
          onRangeChange={setInstantInterval}
          views={[...coachCalendarSupportedViews]}
          view={calendarView}
          onView={onView}
          navigateToDayAt={navigateToDayAt}
          toolbarLeftAddon={
            <SessionScheduleCalendarTimezonePill timeZone={timeZone} dataTestId="my-classes-calendar-timezone" />
          }
        />
      </div>
    </InfoPage>
  );
}
